import moment from "moment-jalaali";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../redux/reducers/root";
import { Booking } from "../../redux/types";
import { getDate } from "../../utils/get-date";
import { getOfficeType } from "../../utils/get-office-details";
import Button from "../shared/button";
import { setBookingCanceled, setBookingChecked, setBookingVisited } from "../../redux/actions/bookings";
import { setModal } from "../../redux/actions/modals";
import { BOOKING_INFO_MODAL } from "../shared/modals/constants";
import Dialog from "../shared/dialog/dialog";
import { useRef, useState } from "react";
import CancelDialog from "../shared/modals/timeslot-detail/cancel-dialog";
import { useGoogleLogin } from "@react-oauth/google";
import { _FAILURE } from "../../redux/constants";

interface Props {
  booking: Booking;
  stateSetter?: any;
  doctorId: number;
}

const BookingItem: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { booking, stateSetter } = props;
  moment.loadPersian();
  const date = booking.timeslot.dateTime;
  const dateString = getDate(parseInt(date), "dddd jYYYY/jM/jD");
  const timeString = getDate(parseInt(date), "HH:mm");
  const userName = booking.user.name;
  const officeId = booking.timeslot.officeId;
  const office = useSelector((state: State) => state.offices.data.find((o) => o.id === officeId));
  const officeName = office?.address?.split("-")[0];
  const officeType = getOfficeType(office!);
  const isOverDue = moment().unix() > parseInt(date);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef();

  const HandleLoadingCard = (response: { type: any }) => {
    const { type } = response;
    type.includes(_FAILURE) && setError("خطا در عملیات");
    setLoading(false);
    stateSetter(true);
  };

  const loginToGoogleAndMakeMeetUrl = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/calendar.events",
    onSuccess: async (tokenResponse) => {
      const response = await dispatch(setBookingChecked(booking.id, tokenResponse.access_token));
      HandleLoadingCard(response);
    },
    onNonOAuthError: () => setLoading(false),
    onError: (error) => console.log("Google login Failed:", error), // eslint-disable-line
  });

  const handleCheckedBooking = async () => {
    setLoading(true);

    if (office?.type === "VID") loginToGoogleAndMakeMeetUrl();
    else {
      const response = dispatch(setBookingChecked(booking.id));
      HandleLoadingCard(response);
    }
  };
  const handleBookingVisited = async (visited: boolean) => {
    await dispatch(setBookingVisited(booking.id, visited));
  };

  const handleCanceledBooking = async (cancelDescription: string = "") => {
    await dispatch(setBookingCanceled(booking.id, cancelDescription, officeId));
    setShowDeleteDialog(false);
    stateSetter(true);
  };

  const handleClickCard = () => {
    dispatch(setModal({ modal: BOOKING_INFO_MODAL, visible: true, data: { booking } }));
  };

  const VisitedStatus = () => {
    return (
      <div className="grid grid-cols-2 gap-2 py-2 m-2">
        <div
          className={`h-full p-3 mx-0 my-0 text-center rounded-md cursor-not-allowed ${
            !booking.visited ? "bg-pkc-red-15 text-pkc-red" : "bg-pkc-primary-15  text-pkc-primary"
          }`}
        >
          {booking.visited ? "مراجعه شده" : "مراجعه نشده"}
        </div>
        <div onClick={() => handleClickCard()} className="p-3 text-center text-blue-900 cursor-pointer ">
          مشاهده جزئیات رزرو
          <i className="mr-2 fas fa-chevron-left" />
        </div>
      </div>
    );
  };

  const CheckedStatus = () => {
    return (
      <div className="grid grid-cols-2 gap-2 py-2 m-2">
        <div
          className={`h-full p-3 mx-0 my-0 text-center rounded-md cursor-not-allowed ${
            !booking.checked ? "bg-pkc-red-15 text-pkc-red" : "bg-pkc-primary-15  text-pkc-primary"
          }`}
        >
          {booking.checked ? "پیگیری شده" : booking.canceled && "لغو شده"}
        </div>
        <div onClick={() => handleClickCard()} className="p-3 text-center text-blue-900 cursor-pointer ">
          مشاهده جزئیات رزرو
          <i className="mr-2 fas fa-chevron-left" />
        </div>
      </div>
    );
  };

  const NullCheckedButton = () => {
    return (
      <div className="grid grid-cols-2 gap-2 py-2 m-2">
        <Button
          disabled={loading}
          className="!text-red-500 !border-red-500 "
          variant="outline"
          onClick={() => setShowDeleteDialog(true)}
        >
          لغو رزرو
        </Button>
        <Button ref={ref as any} disabled={loading} onClick={() => handleCheckedBooking()}>
          پیگیری رزرو
        </Button>
      </div>
    );
  };

  const NullVisitedButton = () => {
    return (
      <div className="grid grid-cols-2 gap-2 py-2 m-2">
        <Button
          className="!text-red-500 !border-red-500 "
          variant="outline"
          onClick={() => handleBookingVisited(false)}
        >
          مراجعه نکرد
        </Button>
        <Button className="" onClick={() => handleBookingVisited(true)}>
          مراجعه کرد
        </Button>
      </div>
    );
  };

  const CanceledStatus = () => {
    return (
      <div className="grid grid-cols-2 gap-2 py-2 m-2">
        <div className="h-full p-3 mx-0 my-0 text-center rounded-md bg-pkc-red-15 text-pkc-red"> لغو شده</div>
        <div onClick={() => handleClickCard()} className="p-3 text-center text-blue-900 ">
          مشاهده جزئیات رزرو
          <i className="mr-2 fas fa-chevron-left" />
        </div>
      </div>
    );
  };

  return (
    <div key={booking.id} className="m-2 bg-white rounded-md shadow card ">
      {loading && (
        <div className="flex justify-center w-full py-2">
          <i className="fa fa-spinner fa-spin fa-2x" />
        </div>
      )}
      <Dialog
        className="overflow-hidden rounded-3xl"
        show={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <CancelDialog
          close={() => setShowDeleteDialog(false)}
          onSubmit={async (d) => await handleCanceledBooking(d.cancelDescription)}
        />
      </Dialog>
      <div className={` ${loading && "opacity-50"}`}>
        <div className="grid grid-cols-2 grid-rows-3 gap-4 p-2" onClick={() => handleClickCard()}>
          <div className="font-bold">{dateString}</div>
          <div className="font-bold">ساعت: {timeString} </div>
          <div className="text-gray-700 "> نام: {userName}</div>
          <div className="md:hidden" />
          <div className="text-gray-700 "> نوع: {officeType} </div>
          {officeName && <div className="text-gray-700 "> مطب: {officeName} </div>}
        </div>
        <hr className="mt-2" />
        {booking.canceled ? (
          <CanceledStatus />
        ) : isOverDue ? (
          booking.visited === null ? (
            <NullVisitedButton />
          ) : (
            <VisitedStatus />
          )
        ) : !booking.checked && !booking.canceled ? (
          <NullCheckedButton />
        ) : (
          <CheckedStatus />
        )}
        {error && <div className="p-1 text-xs text-pkc-red">{error}</div>}
      </div>
    </div>
  );
};

export default BookingItem;
